'use client';

import { Info } from 'lucide-react';
import { Area, AreaChart, CartesianGrid } from 'recharts';

import { useMemo } from 'react';

import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';

import { Tooltip } from '@/shared/components';

import { cn } from '@/lib/utils';

import { fillNullValues } from './chart-utils';
import { CHART_COLORS } from './constants';
import { LockedOverlay } from './locked-overlay';
import { AreaChartCardProps, ChartDataPoint } from './types';

// Dummy data for locked/preview state
const DUMMY_CHART_DATA: ChartDataPoint[] = [
	{ label: 'Jan', value: 45 },
	{ label: 'Feb', value: 52 },
	{ label: 'Mar', value: 48 },
	{ label: 'Apr', value: 65 },
	{ label: 'May', value: 58 },
	{ label: 'Jun', value: 70 },
	{ label: 'Jul', value: 75 },
];

/**
 * Reusable area chart card component with title, value, and recharts AreaChart
 * Used for workspace-level metrics and form-level analytics
 */
export function AreaChartCard({
	title,
	value,
	subtitle,
	helperText,
	data,
	dataKey = 'value',
	isLocked = false,
	isAdminOnly = false,
	isLoading = false,
	showXAxis = false,
	showYAxis = false,
	valueFormatter,
	color = CHART_COLORS.primary,
	className,
	valueClassName,
	filterEmptyDataEntries = false,
	connectEmptyDataPoints = false,
	showDots = false,
	showValueInLockedState = false,
	requiredPlanLabel,
}: AreaChartCardProps) {
	const chartConfig = useMemo(
		() => ({
			[dataKey]: {
				label: title,
				color: color,
			},
		}),
		[dataKey, title, color]
	);

	// Use dummy data when locked to avoid exposing real data in DOM
	// Optionally filter out zero-value entries
	// Apply interpolation/fill for null values when connectEmptyDataPoints is enabled
	const displayData = useMemo(() => {
		if (isLocked) return DUMMY_CHART_DATA.map((p) => ({ ...p, isFilledPoint: false }));

		if (!data) return [];

		// Apply null-filling (interpolation + forward/backward fill) when connecting empty data points
		const filledData = connectEmptyDataPoints
			? fillNullValues(data)
			: data.map((p) => ({ ...p, isFilledPoint: false }));

		if (!filterEmptyDataEntries) return filledData;

		return filledData.filter(
			(point) => point.value !== 0 && point.value !== null && point.value !== undefined
		);
	}, [isLocked, data, filterEmptyDataEntries, connectEmptyDataPoints]);

	const wholeDataEmpty = useMemo(() => {
		return data?.every((point) => point.value === null || point.value === undefined);
	}, [data]);

	// Transform data for recharts
	const chartData = useMemo(() => {
		if (!displayData?.length) return [];
		return displayData.map((point) => ({
			label: point?.label ?? '-',
			labelRange: point?.labelRange ?? point?.label ?? '-',
			[dataKey]: point?.value,
			// Store original value for tooltip display (show 0 for null values)
			originalValue: point?.value ?? 0,
			contextCount: point?.contextCount,
			contextLabel: point?.contextLabel,
			// Track if this point was filled (interpolated/forward/backward) for conditional rendering
			isFilledPoint: point?.isFilledPoint ?? false,
		}));
	}, [displayData, dataKey]);

	// Generate unique gradient ID to avoid conflicts when multiple charts are rendered
	const gradientId = useMemo(
		() => `fill-${dataKey}-${Math.random().toString(36).slice(2, 9)}`,
		[dataKey]
	);

	// Generate dummy value for locked state (1-50 range)
	const dummyValue = useMemo(() => Math.floor(Math.random() * 50) + 1, []);

	// Use dummy value when locked to avoid exposing real data in DOM
	const displayValue = isLocked && !showValueInLockedState ? dummyValue : value;

	if (isLoading) {
		return (
			<div className={cn('rounded-lg border border-border bg-card p-4', className)}>
				<Skeleton className="mb-2 h-4 w-24" />
				<Skeleton className="mb-4 h-8 w-16" />
				<Skeleton className="h-32 w-full" />
			</div>
		);
	}

	const valueDiv = (
		<div className={cn('mb-4')}>
			<p className={cn('text-2xl font-semibold', valueClassName)}>{displayValue}</p>
			{subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
		</div>
	);

	return (
		<div className={cn('rounded-lg border border-border bg-card p-4', className)}>
			{/* Header - Title visible, value blurred when locked */}
			<div className="flex items-center gap-1">
				<p className="text-sm text-muted-foreground">{title}</p>
				{helperText && (
					<Tooltip text={helperText}>
						<Info className="h-3.5 w-3.5 cursor-default text-muted-foreground/70 transition-colors hover:text-muted-foreground" />
					</Tooltip>
				)}
			</div>
			{showValueInLockedState || !isLocked ? valueDiv : null}

			{/* Chart - Wrapped with locked overlay */}
			<LockedOverlay
				isLocked={isLocked}
				isAdminOnly={isAdminOnly}
				requiredPlanLabel={requiredPlanLabel}
			>
				{!showValueInLockedState && isLocked ? valueDiv : null}
				{displayData?.length === 0 || wholeDataEmpty ? (
					<div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
						No data available
					</div>
				) : (
					<ChartContainer
						config={chartConfig}
						className={cn('h-32 [&]:aspect-auto', isLocked ? '' : '-translate-x-1')}
					>
						<AreaChart
							data={chartData}
							margin={{ top: 5, right: 0, bottom: 0, left: showYAxis ? 12 : 0 }}
						>
							<defs>
								<linearGradient
									id={gradientId}
									x1="0"
									y1="0"
									x2="0"
									y2="1"
								>
									<stop
										offset="5%"
										stopColor={color}
										stopOpacity={0.8}
									/>
									<stop
										offset="95%"
										stopColor={color}
										stopOpacity={0.1}
									/>
								</linearGradient>
							</defs>
							{(showXAxis || showYAxis) && (
								<CartesianGrid
									strokeDasharray="3 3"
									vertical={false}
								/>
							)}
							<ChartTooltip
								cursor={false}
								content={
									<ChartTooltipContent
										labelFormatter={(_, payload) => {
											// Show date range in tooltip
											const labelRange = payload?.[0]?.payload?.labelRange;
											return labelRange ?? '-';
										}}
										formatter={(_tooltipValue, _name, item) => {
											// Show "No data" for filled points (interpolated/forward/backward filled)
											if (item?.payload?.isFilledPoint) {
												return <span className="text-muted-foreground">No data</span>;
											}

											// Use originalValue for display (tooltipValue may be null for zero values)
											const numValue = item?.payload?.originalValue ?? 0;
											const formattedValue = valueFormatter
												? valueFormatter(numValue)
												: String(numValue);
											const contextCount = item?.payload?.contextCount;
											const contextLabel = item?.payload?.contextLabel as string | undefined;

											// If we have context count, show it below the value
											if (contextCount != null && contextLabel) {
												// Only pluralize nouns (not past participles like 'reviewed', 'responded')
												const needsPlural =
													contextCount !== 1 &&
													!contextLabel.endsWith('ed') &&
													!contextLabel.endsWith('s');
												const displayLabel = needsPlural ? `${contextLabel}s` : contextLabel;
												return (
													<span className="flex flex-col gap-0.5">
														<span>{formattedValue}</span>
														<span className="text-xs text-muted-foreground">
															{contextCount} {displayLabel}
														</span>
													</span>
												);
											}
											return formattedValue;
										}}
									/>
								}
							/>
							<Area
								type="monotone"
								dataKey={dataKey}
								stroke={color}
								fill={`url(#${gradientId})`}
								fillOpacity={0.4}
								strokeWidth={2}
								dot={
									showDots
										? (props: {
												cx?: number;
												cy?: number;
												payload?: { isFilledPoint?: boolean };
											}) => {
												// Don't render dots for filled points (interpolated/forward/backward filled)
												// Return invisible circle instead of null for type compatibility
												if (props.payload?.isFilledPoint) {
													return <circle r={0} />;
												}
												if (props.cx === undefined || props.cy === undefined) {
													return <circle r={0} />;
												}

												return (
													<circle
														cx={props.cx}
														cy={props.cy}
														r={2}
														stroke={color}
														fill={color}
														fillOpacity={1}
														strokeWidth={2}
													/>
												);
											}
										: undefined
								}
							/>
						</AreaChart>
					</ChartContainer>
				)}
			</LockedOverlay>
		</div>
	);
}
